import type { Listing } from '@/types/listing';
import type { RecentSearch } from './recentSearches';
import type { RecentlyViewedItem } from './recentlyViewed';

/**
 * "Pick up where you left off" — the visitor's own two trails, both kept in
 * localStorage: the searches they ran (`recentSearches`) and the homes they
 * opened (`recentlyViewed`).
 *
 * Why this is a separate module from `personalFeed`: those rails need an account
 * (saved searches, favourites, a server round-trip that can 401). These need
 * nothing but this browser, so they work for a signed-out visitor on their second
 * visit — which is the majority of returning traffic. (W14 R137)
 */

/** Chips shown for past searches. Four fits one row at 390 without wrapping twice. */
export const RESUME_CHIPS = 4;
/** Cards in the viewed-homes rail. */
export const RESUME_VIEWED_CARDS = 8;

/**
 * How far back "where you left off" reaches.
 *
 * A home opened in March is not somewhere the visitor left off in July, and
 * offering it back reads as a site that has confused its own history for
 * interest. `recentlyViewed` caps at 12 entries with no clock, so without this an
 * abandoned tab from months ago outranks yesterday's real search.
 */
export const RESUME_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

function isFresh(at: number | undefined, nowMs: number): boolean {
  return typeof at === 'number' && at > 0 && nowMs - at <= RESUME_MAX_AGE_MS;
}

/** Recent searches worth offering back, newest first. */
export function resumeChips(
  searches: RecentSearch[],
  nowMs: number,
  limit = RESUME_CHIPS
): RecentSearch[] {
  return searches
    .filter((s) => s && s.label && s.href && isFresh(s.at, nowMs))
    .sort((a, b) => b.at - a.at)
    .slice(0, limit);
}

/**
 * The ids to ask the API about, newest view first and de-duplicated.
 *
 * Order matters downstream: `getListingsByIds` preserves the order it is given,
 * so this is what puts the last home the visitor looked at first in the rail.
 */
export function resumeViewedIds(
  items: RecentlyViewedItem[],
  nowMs: number,
  limit = RESUME_VIEWED_CARDS
): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const i of [...items].sort((a, b) => b.viewedAt - a.viewedAt)) {
    if (!i?.id || seen.has(i.id) || !isFresh(i.viewedAt, nowMs)) continue;
    seen.add(i.id);
    out.push(i.id);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * The viewed rail, from live listings rather than the localStorage snapshot.
 *
 * The snapshot carries a **price recorded at view time**, and a home whose price
 * moved since would be shown at its old number on the same page that has a rail
 * about price changes. Re-reading them live also drops the ones that have gone
 * off-market: `getListingsByIds` returns nothing for a sold or withdrawn listing,
 * so "pick up where you left off" cannot offer a dead end.
 *
 * @param ids   view order, from `resumeViewedIds`
 * @param live  whatever the API still has, in any order
 */
export function resumeViewedRail(ids: string[], live: Listing[]): Listing[] {
  const byId = new Map(live.map((l) => [l.id, l]));
  return ids.map((id) => byId.get(id)).filter((l): l is Listing => l !== undefined);
}

/**
 * Has this browser been here before?
 *
 * Drives the pitch collapse (`body[data-returning]`): someone who has searched or
 * opened a home here has already converted on the idea and does not need the
 * $5,000 proposition re-argued above the feed. Deliberately **not** age-bounded,
 * unlike the rails — a visitor from three months ago has still heard the pitch, so
 * the right page for them is the feed even when there is nothing fresh enough to
 * offer back.
 */
export function hasBrowsingHistory(
  searches: RecentSearch[],
  viewed: RecentlyViewedItem[]
): boolean {
  return searches.length > 0 || viewed.length > 0;
}
